import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "../../api/client";
import { chatApi, type AccessCardView } from "../api";
import { UI, useLang, type Bilingual } from "../lang";

const S = {
  asks: { en: "asks for access for this agent.", ar: "يطلب الوصول لهذا الوكيل." },
  access: { en: "What it will access", ar: "ما سيصل إليه" },
  explain: {
    en: "You're approving what it may read and who sees it. Whether its figures are right is checked by the evals while it runs as a Trial.",
    ar: "أنت توافق على ما يمكنه قراءته ومن يراه. أما صحة أرقامه فتتحقق منها التقييمات أثناء عمله كتجربة.",
  },
  decision: { en: "Decision", ar: "القرار" },
  approve: { en: "Approve access", ar: "الموافقة على الوصول" },
  approveShare: { en: "Approve sharing", ar: "الموافقة على المشاركة" },
  approvePublish: { en: "Approve publishing", ar: "الموافقة على النشر" },
  libraryLabel: { en: "Library request", ar: "طلب نشر في المكتبة" },
  explainLibrary: {
    en: "You're approving what it may read and which roles may reuse it without asking again. Each copy is its own agent, scored on its own runs.",
    ar: "أنت توافق على ما يمكنه قراءته وأي الأدوار يمكنها إعادة استخدامه دون طلب جديد. كل نسخة وكيل مستقل يُقيَّم على تشغيلاته.",
  },
  closed: { en: "This request was closed before it was decided.", ar: "أُغلق هذا الطلب قبل البتّ فيه." },
  no: { en: "Say no", ar: "رفض" },
  reasonHint: { en: "A sentence — at least 10 characters.", ar: "جملة — ١٠ أحرف على الأقل." },
  recorded: { en: "Recorded.", ar: "سُجّل." },
  stale: {
    en: "The agent changed what it reads since this request. A new request will ask again.",
    ar: "تغيّر ما يقرؤه الوكيل منذ هذا الطلب. سيُطلب الوصول من جديد بطلب جديد.",
  },
} satisfies Record<string, Bilingual>;

/** The decided state, from the request's row: who decided and why. */
function decided(card: AccessCardView): Bilingual | null {
  const who = card.decided_by_name ?? "";
  const why = card.reason ?? "";
  if (card.status === "approved") return { en: `Approved by ${who}: “${why}”`, ar: `وافق ${who}: «${why}»` };
  if (card.status === "rejected") return { en: `${who} said no: “${why}”`, ar: `رفض ${who}: «${why}»` };
  return null;
}

/**
 * A new agent's access, put to a Supervisor (agent-library Task 8): who asks,
 * for which agent, what it will do, and the five access lines the requester
 * saw. Approve or say no, each with a reason of a sentence, as on decision
 * cards. What it approves is access, not the agent's figures: the line under
 * the five says so.
 */
export function AccessCard({ workspaceId, card }: { workspaceId: string; card: AccessCardView }) {
  const { t, lang } = useLang();
  const qc = useQueryClient();
  const [decision, setDecision] = useState<"approved" | "rejected" | null>(null);
  const [reason, setReason] = useState("");
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);
  const decide = useMutation({
    mutationFn: () => chatApi.decideAccess(workspaceId, card.request_id, { decision: decision!, reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["chat-thread", workspaceId] });
      void qc.invalidateQueries({ queryKey: ["chat-queue", workspaceId] });
    },
  });
  // Choosing is the start of deciding: bring the reason box into view and focus it.
  useEffect(() => {
    if (!decision) return;
    const el = reasonRef.current;
    if (!el) return;
    el.focus();
    el.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [decision]);

  const id = `access-${card.request_id}`;
  const lines = lang === "ar" && card.access.ar.length > 0 ? card.access.ar : card.access.en;
  const done = decided(card);
  const open = card.status === "open" && !card.stale && !decide.isSuccess;
  // A share (Task 13): the same decision, on who else sees it.
  const shareWords = card.shared_with_words ?? null;
  // A library request (Task 13b): the same decision, on publishing it for others to reuse.
  const library = card.library ?? null;
  const asks: Bilingual = library
    ? { en: `asks to publish this agent to the library for ${library.roles_words.en}.`, ar: `يطلب نشر هذا الوكيل في المكتبة لـ${library.roles_words.ar}.` }
    : shareWords
      ? { en: `asks to share this agent with ${shareWords.en}.`, ar: `يطلب مشاركة هذا الوكيل مع ${shareWords.ar}.` }
      : S.asks;
  const choices = [
    { key: "approved" as const, label: library ? S.approvePublish : shareWords ? S.approveShare : S.approve },
    { key: "rejected" as const, label: S.no },
  ];

  return (
    <div className="pwa-card pwa-fork pwa-access" data-testid={id}>
      {library ? <div className="pwa-fork-prepared-label">{t(S.libraryLabel)}</div> : null}
      <div className="ttl">{t(card.agent_name)}</div>
      <div className="sub">
        <bdi>{card.requester_name}</bdi> {t(asks)}
      </div>
      <div style={{ margin: "4px 0" }}>{t(card.in_words)}</div>
      <div className="pwa-fork-prepared-label">{t(S.access)}</div>
      <ul className="pwa-spec-access">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
      <div className="sub">{t(library ? S.explainLibrary : S.explain)}</div>

      {/* Stale first: a request superseded (the agent changed, was retired, or a library request was replaced) was decided by nobody. */}
      {card.stale ? (
        <div className="pwa-fork-effect" data-testid={`${id}-state`}>
          {t(card.closed ? S.closed : S.stale)}
        </div>
      ) : done ? (
        <div className="pwa-fork-effect" data-testid={`${id}-state`}>
          {t(done)}
        </div>
      ) : null}

      {open ? (
        <>
          <div className="pwa-choices" role="group" aria-label={t(S.decision)}>
            {choices.map((c) => (
              <button
                key={c.key}
                type="button"
                className={`pwa-btn${decision === c.key ? " on" : ""}`}
                aria-pressed={decision === c.key}
                data-testid={`${id}-${c.key}`}
                onClick={() => setDecision(c.key)}
              >
                {t(c.label)}
              </button>
            ))}
          </div>
          {decision ? (
            <>
              <textarea
                ref={reasonRef}
                className="pwa-reason"
                data-testid={`${id}-reason`}
                placeholder={t(UI.reasonPlaceholder)}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              {reason.trim().length < 10 ? <div className="sub pwa-reason-hint">{t(S.reasonHint)}</div> : null}
              <div className="pwa-fork-submit">
                <button
                  type="button"
                  className="pwa-btn primary"
                  data-testid={`${id}-submit`}
                  disabled={decide.isPending || reason.trim().length < 10}
                  onClick={() => decide.mutate()}
                >
                  {decide.isPending ? t(UI.recording) : t(UI.record)}
                </button>
              </div>
            </>
          ) : null}
        </>
      ) : null}
      {decide.isSuccess ? (
        <div className="sub" role="status">
          {t(S.recorded)}
        </div>
      ) : null}
      {decide.isError ? (
        <div className="sub" role="alert" style={{ color: "var(--danger)", marginTop: 6 }}>
          {apiErrorMessage(decide.error)}
        </div>
      ) : null}
    </div>
  );
}
